// apps/account-portal/src/App.tsx
import React, { useEffect, useLayoutEffect, useRef, useContext, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { redirectToLogin } from '@redpanda/auth-utils';
import { PUBLIC_PATHS } from './constants/publicPaths';
import SystemOverviewPage from './pages/SystemOverviewPage';
import SubscriptionPage from './pages/SystemSubscriptionPage';
import BillingHistoryPage from './pages/BillingHistoryPage';
import HelpPage from './pages/HelpPage';
import ProfilePage from './pages/ProfilePage';
import AdminPage from './pages/AdminPage';
import CustomServicePage from './pages/CustomServicePage';
import BoardPage from './pages/BoardPage';
import WishlistPage from './pages/WishlistPage';
import ServiceCategoryPage from './pages/ServiceCategoryPage';
import AppsPage from './pages/AppsPage';
import GamesPage from './pages/GamesPage';
import ContactPage from './pages/ContactPage';
import PrivateRoute from './components/PrivateRoute';
import PageFooter from './components/PageFooter';
import { TermsOfServicePage, PrivacyPolicyPage, RefundPolicyPage } from '@redpanda/ui';
import Sidebar from './components/Sidebar';
import Seo from './components/Seo';
import { UserProvider, UserContext } from './context/UserContext';
import { ColorThemeProvider } from './context/ColorThemeContext';
import { useSubscriptionStore } from './stores/subscriptionStore';
import { SystemType } from '@redpanda/types';
import { Toaster } from 'react-hot-toast';
import { Bars3Icon } from '@heroicons/react/24/outline';

// 捲動位置管理：切換頁面時回到頂端；重新整理時還原原本的捲動位置
// 交給瀏覽器自動還原會先顯示頂端再跳下去（React 晚於瀏覽器還原時機才渲染完成），
// 改成離開頁面時記下位置、首次渲染後於繪製前（useLayoutEffect）還原
const SCROLL_KEY_PREFIX = 'scroll:';

const ScrollManager: React.FC = () => {
  const { pathname } = useLocation();
  // 記上一個路徑而非「是否首次」：開發模式 StrictMode 會重跑 effect，同路徑重跑時不能被當成換頁
  const prevPathname = useRef<string | null>(null);

  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    const save = () => {
      try {
        sessionStorage.setItem(SCROLL_KEY_PREFIX + window.location.pathname, String(window.scrollY));
      } catch { /* sessionStorage 不可用時略過 */ }
    };
    window.addEventListener('pagehide', save);
    return () => window.removeEventListener('pagehide', save);
  }, []);

  useLayoutEffect(() => {
    const prev = prevPathname.current;
    prevPathname.current = pathname;
    if (prev === pathname) return;
    if (prev === null) {
      try {
        const saved = sessionStorage.getItem(SCROLL_KEY_PREFIX + pathname);
        if (saved) window.scrollTo(0, Number(saved));
      } catch { /* sessionStorage 不可用時略過 */ }
      return;
    }
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// /login 路由重定向到 identity-service
const LoginRedirect: React.FC = () => {
  useEffect(() => {
    const identityServiceUrl = process.env.REACT_APP_IDENTITY_SERVICE_URL || 'http://localhost:3000';
    // 登入後返回首頁
    const returnUrl = window.location.origin + '/';
    redirectToLogin(identityServiceUrl, returnUrl);
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--page-bg)]">
      <Seo
        title="會員登入 | RedPanda Tech 小熊貓科技"
        description="登入 RedPanda Tech 帳戶，管理您的訂閱服務、帳單與各系統存取權限。"
        path="/login"
      />
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--accent)] mx-auto mb-4"></div>
        <p className="text-[var(--text-secondary)]">正在前往登入頁面...</p>
      </div>
    </div>
  );
};

// Catch-all 重定向組件
const RedirectToHome: React.FC = () => {
  const navigate = useNavigate();
  useEffect(() => {
    navigate('/', { replace: true });
  }, [navigate]);
  return null;
};

const AppContent: React.FC = () => {
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  return (
    <div className="App bg-[var(--page-bg)] min-h-screen flex transition-colors duration-300">
      <Toaster position="bottom-right" />
      <ScrollManager />

      {/* 手機版頂部列：實底，避免捲動時內容被漢堡按鈕蓋住；高度 h-14 對應 main 的 pt-14 */}
      <header className="md:hidden fixed top-0 inset-x-0 h-14 z-30 flex items-center gap-2 px-2 bg-[var(--page-bg)] border-b border-[var(--border-c)]">
        <button
          onClick={() => setIsMobileSidebarOpen(true)}
          className="p-2 rounded-lg hover:bg-[var(--surface)] transition-colors"
        >
          <Bars3Icon className="h-6 w-6 text-[var(--text-secondary)]" />
        </button>
        <Link to="/" className="text-base font-bold text-[var(--text-primary)]">RedPanda Tech</Link>
      </header>

      {/* 左側導覽列 */}
      <Sidebar
        className="w-48 h-screen fixed left-0 top-0 z-50"
        isMobileOpen={isMobileSidebarOpen}
        onCloseMobile={() => setIsMobileSidebarOpen(false)}
      />

      {/* 主要內容區域 */}
      <div className="flex-1 flex flex-col md:ml-48 min-h-screen bg-[var(--page-bg)]">
        <main className="flex-grow min-h-screen pt-14 md:pt-0">
          <Routes>
          <Route path="/" element={<SystemOverviewPage />} />

          {/* 服務分類頁面 - 公開路由，無需登入 */}
          <Route path="/enterprise" element={<ServiceCategoryPage category="enterprise" />} />
          <Route path="/store" element={<ServiceCategoryPage category="store" />} />

          {/* 各系統方案與價格：公開瀏覽，選方案時才要求登入（SystemSubscriptionPage） */}
          <Route path="/crm" element={<SubscriptionPage system="CRM" />} />
          <Route path="/accounting" element={<SubscriptionPage system="ACCOUNTING" />} />
          <Route path="/pm" element={<SubscriptionPage system="PM" />} />
          <Route path="/hr" element={<SubscriptionPage system="HR" />} />
          <Route path="/booking" element={<SubscriptionPage system="BOOKING" />} />
          <Route path="/queue" element={<SubscriptionPage system="QUEUE" />} />


          {/* 系統特定方案選擇 - 已整合到subscription頁面，Stripe風格 */}

          {/* 其他功能頁面 - checkout已整合為Modal */}
          <Route path="/billing-history" element={
            <PrivateRoute>
              <BillingHistoryPage />
            </PrivateRoute>
          } />
          <Route path="/help" element={
            <PrivateRoute>
              <HelpPage />
            </PrivateRoute>
          } />
          <Route path="/profile" element={
            <PrivateRoute>
              <ProfilePage />
            </PrivateRoute>
          } />
          <Route path="/admin" element={
            <PrivateRoute>
              <AdminPage />
            </PrivateRoute>
          } />
          {/* 外包服務頁面 - 公開路由，無需登入 */}
          <Route path="/custom-service" element={<CustomServicePage />} />

          {/* 服務條款、隱私權、退款政策：公開（共用 @redpanda/ui LegalPage） */}
          <Route path="/terms" element={<TermsOfServicePage footer={<PageFooter />} />} />
          <Route path="/privacy" element={<PrivacyPolicyPage footer={<PageFooter />} />} />
          <Route path="/refund-policy" element={<RefundPolicyPage footer={<PageFooter />} />} />
          <Route path="/contact" element={<ContactPage />} />

          {/* 留言板 & 許願清單 - 公開路由，訪客可瀏覽與留言 */}
          <Route path="/board" element={<BoardPage />} />
          <Route path="/wishlist" element={<WishlistPage />} />

          {/* 行動 App - 公開路由，無需登入 */}
          <Route path="/apps" element={<AppsPage />} />

          {/* 遊戲 - 公開路由，無需登入 */}
          <Route path="/games" element={<GamesPage />} />

          {/* /login 路由 - 重定向到 identity-service */}
          <Route path="/login" element={
            <LoginRedirect />
          } />

          {/* Catch-all 路由 - 重定向到首頁 */}
          <Route path="*" element={<RedirectToHome />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

const ALL_SYSTEMS: SystemType[] = ['CRM', 'ACCOUNTING', 'PM', 'HR', 'BOOKING', 'QUEUE'];

const ThemedApp: React.FC = () => {
  const { isLoading, isAuthenticated } = useContext(UserContext)!;
  const location = useLocation();
  const subscriptionStore = useSubscriptionStore();

  // 登入後立刻在背景預載所有系統方案（靜態資料），確保訂閱管理頁進入時無需等待
  useEffect(() => {
    if (!isAuthenticated) return;
    ALL_SYSTEMS.forEach(system => subscriptionStore.fetchSystemPlans(system));
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const isPublicPath = PUBLIC_PATHS.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));

  // 公開路徑、或已有快取登入狀態時，跳過 Logo 載入畫面直接渲染
  if (isLoading && !isPublicPath && !isAuthenticated) {
    return (
      <div className="app-loading bg-[var(--page-bg)] min-h-screen flex items-center justify-center">
        <div className="text-center">
          <img
            src="/logo.png"
            alt="RedPanda Tech"
            className="w-48 h-48 mx-auto mb-6 object-contain"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.parentElement!.querySelector('.logo-fallback')!.classList.remove('hidden');
            }}
          />
          <div className="logo-fallback hidden text-2xl font-semibold text-[var(--text-primary)] mb-6">RedPanda Tech</div>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--accent)] mx-auto"></div>
        </div>
      </div>
    );
  }

  return <AppContent />;
};

function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ColorThemeProvider>
        <UserProvider>
          <ThemedApp />
        </UserProvider>
      </ColorThemeProvider>
    </Router>
  );
}

export default App;
